import {
  Button,
  CellFormatOptions,
  ColumnTable,
  Table,
} from '@octano/global-ui';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';

import { Section } from '../../../api/requests/MantainerPortal';
import TableEmptyContent from '../../../components/text/TableEmptyContent';
import { PathsLayouts } from '../../../config/routes';
import { ITEMS_PER_PAGE_DEFAULT } from '../api';

interface TableCEClosingPeriodProps {
  state: string;
  data: any[];
  isLoadingResults: boolean;
  totalItems: number;
  totalPages: number;
  currentPage: number;
  onChangePage: (page: number) => void;
  hasFilters?: boolean;
}

export function TableCEClosingPeriod(props: TableCEClosingPeriodProps) {
  const { t } = useTranslation();
  const prefix = 'ceClosingPeriod.table';
  const history = useHistory();

  const {
    state,
    data,
    isLoadingResults,
    totalItems,
    totalPages,
    currentPage,
    onChangePage,
  } = props;

  const defaultColumns = useMemo<ColumnTable[]>(
    () => [
      {
        headerText: t(`${prefix}.year`),
        columnName: 'year',
        tdClassName: 'text-center',
        thClassName: 'text-center',
        cellFormat: (options: CellFormatOptions<Section>) => {
          return options.row.sectionPackages
            ?.map((sectionPackage) => {
              return sectionPackage.academicOfferStudyPlanVersionOffer.duration;
            })
            .join(', ');
        },
      },
      {
        headerText: t(`${prefix}.offer`),
        columnName: 'offer',
        tdClassName: 'text-center',
        thClassName: 'text-center',
        cellFormat: (options: CellFormatOptions<Section>) => {
          return options.row.sectionPackages
            ?.map((sectionPackage) => {
              return sectionPackage.academicOfferStudyPlanVersionOffer
                .studyPlanVersionOffer.studyPlanVersion.name;
            })
            .join(', ');
        },
      },
      {
        headerText: t(`${prefix}.modalityName`),
        columnName: 'modalityName',
        tdClassName: 'text-center',
        thClassName: 'text-center',
        cellFormat: (options: CellFormatOptions<Section>) => {
          return options.row.course.modality.name;
        },
      },
      {
        headerText: t(`${prefix}.Subject`),
        columnName: 'courseName',
        tdClassName: 'text-center',
        thClassName: 'text-center',
        cellFormat: (options: CellFormatOptions<Section>) => {
          return options.row.course.name;
        },
      },
      {
        headerText: t(`${prefix}.schoolName`),
        columnName: 'schoolName',
        tdClassName: 'text-center',
        thClassName: 'text-center',
        cellFormat: (options: CellFormatOptions<Section>) => {
          return options.row.course.school.name;
        },
      },
      {
        headerText: t(`${prefix}.campus`),
        columnName: 'campusName',
        tdClassName: 'text-center',
        thClassName: 'text-center',
        cellFormat: (options: CellFormatOptions<Section>) => {
          return options.row.campus.name;
        },
      },
      {
        columnName: 'name',
        headerText: t(`${prefix}.section`),
        tdClassName: 'text-center',
        thClassName: 'text-center',
      },
      {
        columnName: 'teacher',
        headerText: t(`${prefix}.teacher`),
        tdClassName: 'text-center',
        thClassName: 'text-center',
        cellFormat: (options: CellFormatOptions<Section>) => {
          return options.row.professors.slice(0, 3).map(({ account }) => {
            return <p key={`teacher-${account.email}`}>{account.fullName}</p>;
          });
        },
      },
      {
        columnName: 'email',
        headerText: t(`${prefix}.email`),
        tdClassName: 'text-center',
        thClassName: 'text-center',
        cellFormat: (options: CellFormatOptions<Section>) => {
          return options.row.professors.slice(0, 3).map(({ account }) => {
            return <p key={`email-${account.email}`}>{account.email}</p>;
          });
        },
      },
    ],
    [t],
  );

  const percentageSummaryColumn = useMemo<ColumnTable>(() => {
    return {
      columnName: 'percentageApproved',
      headerText: t(`${prefix}.percentageApproved`),
      tdClassName: 'text-center',
      thClassName: 'text-center',
      cellFormat: (options: CellFormatOptions<Section>) => {
        return `${Math.round(
          options.row.closedWith?.passPercentage as number,
        )}%`;
      },
    };
  }, [t]);

  const actionColumn = useMemo<ColumnTable>(() => {
    return {
      columnName: 'action',
      headerText: t(`${prefix}.action`),
      tdClassName: 'text-center',
      thClassName: 'text-center',
      cellFormat: (options: CellFormatOptions<Section>) => {
        return (
          <Button
            text={t(`${prefix}.btnSeeClosedSection`)}
            size="sm"
            onClick={() => {
              history.push(
                `${PathsLayouts.nonStructured}/closing-period/${options.row.id}`,
              );
            }}
          />
        );
      },
    };
  }, [t, history]);

  const columns = useMemo<ColumnTable[]>(() => {
    const array = defaultColumns;
    if (state !== 'open') {
      array.push(percentageSummaryColumn);
    }
    array.push(actionColumn);
    return array;
  }, [state, defaultColumns, percentageSummaryColumn, actionColumn]);

  const noResultsText = useMemo(() => {
    if (state === 'open') {
      return (
        <TableEmptyContent
          title={t(`${prefix}.notResultStateOpen.title`)}
          subtitle={t(`${prefix}.notResultStateOpen.subtitle`)}
        />
      );
    }
    return (
      <TableEmptyContent
        title={t(`${prefix}.notResult.title`)}
        subtitle={t(`${prefix}.notResult.subtitle`)}
      />
    );
  }, [t, state]);

  return (
    <Table
      columns={columns}
      data={data}
      pagination={{
        currentPage: currentPage,
        itemsPerPage: ITEMS_PER_PAGE_DEFAULT,
        totalItems,
        totalPages,
        onChangePage: (page) => onChangePage(page),
      }}
      isLoadingResults={isLoadingResults}
      noResultsText={noResultsText}
    />
  );
}
